import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { 
  BarChart3, 
  Rocket, 
  Eye, 
  Heart, 
  MapPin, 
  Zap 
} from 'lucide-react';

const AdvantagesSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const icons = [BarChart3, Rocket, Eye, Heart, MapPin, Zap];
  const colors = [
    'bg-[#2563EB]/10 text-[#2563EB]',
    'bg-violet-100 text-violet-600',
    'bg-cyan-100 text-cyan-600',
    'bg-rose-100 text-rose-600',
    'bg-emerald-100 text-emerald-600',
    'bg-orange-100 text-orange-600'
  ];
  
  return (
    <section 
      id="advantages" 
      ref={ref}
      className="section-spacing relative bg-white"
      data-testid="advantages-section"
    >
      <div className="section-container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase mb-4 block">
            {t.advantages.subtitle}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] max-w-3xl mx-auto">
            {t.advantages.title}
          </h2>
        </motion.div>
        
        {/* Advantages Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.advantages.items.map((item, index) => {
            const Icon = icons[index];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-light rounded-2xl p-6 card-hover group"
                data-testid={`advantage-${index}`}
              >
                <div className={`w-12 h-12 rounded-xl ${colors[index]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-[#475569] leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AdvantagesSection;
